//
// # Symbol — Render Tier
//
// The DOM-free render core for a symbol: pins, expression labels, selection
// boxes, annotation graphics, and its redraw. Kept in its own module (no
// modes/keys/interaction imports) so the headless server can build and redraw a
// symbol without pulling in the browser interaction tier. The interaction facade
// `Symbol` (in `symbol.ts`) owns a `SymbolRender` and delegates to it.
//

// Local Imports
import { schema } from "GenAlphaSchema";
import { RenderContentBase } from "../base_render";
import { ContentKind } from "../kind";
import { ExprLabel, SymbolPin, Annotation, AnnotationsRender } from "../shared";
import { SelectionBoxEntity } from "./selectionbox";
import { IdMap } from "../../ids";
import { DrawTo } from "../../draw";
import { StyleTables } from "../shared/styles";

// The render-tier child set (drawn entities). `Selectable` (editing granularity)
// lives with the interaction facade in `symbol.ts`.
export type Child = SymbolPin | ExprLabel | Annotation | SelectionBoxEntity;

export class SymbolRender extends RenderContentBase {
  override readonly contentKind = ContentKind.Symbol;

  // Internal data stores
  readonly pins = new IdMap<SymbolPin>();
  readonly exprLabels = new IdMap<ExprLabel>();
  readonly selectionBoxEntitys = new IdMap<SelectionBoxEntity>();
  readonly annotations = new AnnotationsRender(this, StyleTables.symbol);

  // Provisional entities — in-progress, not yet committed.
  // Tracked here so that redraw() includes them for rendering.
  readonly provisionals = new Set<Child>();

  // PDK identifier
  public pdkId?: string;
  // Forward references to schematics that implement this symbol
  public schematicImpls?: Array<schema.symbol.SchematicImplRef>;

  // Recompute bounding box from current entities.
  // Symbols don't currently track a scene bbox, so this is a no-op.
  override recalcBbox = (): void => {};

  // (Re)draw the `Symbol`
  override redraw = (to: DrawTo): unknown => {
    this.annotations.redraw(to); // Annotation graphics sub-group

    // Draw each selection box
    for (const boxEntity of this.selectionBoxEntitys.values()) {
      boxEntity.redraw(to);
    }

    // Draw each of our `SymbolPin`s
    for (const pin of this.pins.values()) {
      pin.redraw(to);
    }
    // Draw each of our `Label`s
    for (const exprLabel of this.exprLabels.values()) {
      exprLabel.redraw(to);
    }
    // Draw provisional (in-progress) entities
    for (const entity of this.provisionals) {
      entity.redraw(to);
    }
    return null;
  };
}
